<template>
  <el-form :inline="true" class="custom-enum-state-form" @submit.native.prevent>
    <el-form-item>
      <div class="el-input-group el-input-group--prepend">
        <div class="el-input-group__prepend">Scope</div>
        <el-select v-model="localEnumState.scope" placeholder="Select">
          <el-option
            v-for="scope in availableScopes"
            :key="scope.id"
            :label="scope.label"
            :value="scope.id"
          />
        </el-select>
      </div>
    </el-form-item>
    <el-form-item>
      <el-input
        class="custom-state-input"
        placeholder="Enter an enum name"
        :value="localEnumState.label"
        :maxlength="50"
        :disabled="readonly"
        @input="v => updateLabel(v.trim())"
      >
        <template slot="prepend">Name:</template>
      </el-input>
    </el-form-item>
    <el-form-item>
      <div
        v-for="(value, index) in localEnumState.values"
        :key="value.id"
        class="value-row"
      >
        <el-input
          class="custom-state-input"
          placeholder="Enter an enum value"
          :value="value.label"
          :maxlength="50"
          :disabled="readonly"
          @input="v => updateValue(index, v.trim())"
        >
          <template slot="prepend">{{ index + 1 }}</template>
        </el-input>
        <el-button
          v-if="!value.isNew"
          :disabled="readonly || customEnumState.value === value.id"
          icon="delete"
          size="small"
          @click="removeValue(index)"
        />
      </div>
    </el-form-item>

    <el-form-item>
      <div class="error-message">
        {{ errorMessage || '\u00A0' }}
      </div>
    </el-form-item>

    <input type="submit" style="display: none;">

    <el-form-item class="button-row">
      <el-button type="primary" @click="onApply">{{ applyLabel }}</el-button>
      <el-button @click="onCancel">Cancel</el-button>
    </el-form-item>
  </el-form>
</template>

<script>
import Chance from 'chance';
import cloneDeep from 'lodash/cloneDeep';

import scopeMixins from '../utils/ScopeMixins';

const chance = new Chance();

export default {
  name: 'custom-enum-state-editor',
  mixins: [ scopeMixins ],
  props: {
    caseConf: {
      type: Object,
      default: () => {}
    },
    customEnumState: {
      type: Object,
      default: () => {}
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    applyLabel: {
      type: String,
      default: 'Apply'
    },
  },
  data() {
    return {
      localEnumState: null,
      errorMessage: null,
    };
  },
  computed: {
    availableScopes() {
      return this.getAvailableScopes(this.caseConf);
    },
  },
  watch: {
    customEnumState() {
      this.syncState();
    }
  },
  created() {
    this.syncState();
  },
  methods: {
    syncState() {
      this.localEnumState = cloneDeep(this.customEnumState);
      this.commonUpdate();
    },
    commonUpdate() {
      this.errorMessage = null;
      const lastValue = this.localEnumState.values.at(-1); // gets last element
      if (!lastValue || (!lastValue.isNew && this.localEnumState.values.length < 10)) {
        // Create empty entry user can start typing in
        this.localEnumState.values.push({
          id: chance.guid(),
          label: '',
          isNew: true,
        });
      }
    },
    updateLabel(label) {
      this.localEnumState.label = label;
      this.commonUpdate();
    },
    updateValue(index, text) {
      const enumValue = this.localEnumState.values[index];
      if (enumValue) {
        enumValue.label = text;
        if (text && enumValue.isNew) {
          enumValue.isNew = undefined;
        }
      }
      this.commonUpdate();
    },
    removeValue(index) {
      this.localEnumState.values.splice(index, 1);
      this.commonUpdate();
    },
    onApply() {
      if (!this.localEnumState.label) {
        this.errorMessage = 'Enter a name';
        return;
      }
      const nonEmptyValues = this.localEnumState.values.filter(value => !value.isNew && value.label);
      if (nonEmptyValues.length === 0) {
        this.errorMessage = 'Enter at least one value';
        return;
      }
      for (const value of this.localEnumState.values) {
        if (!value.isNew && !value.label) {
          this.errorMessage = 'Entered values cannot be empty';
          return;
        }
      }
      const uniqueLabelCount = new Set(this.localEnumState.values.map(v => v.label)).size;
      const totalLabelCount = this.localEnumState.values.length;
      if (uniqueLabelCount !== totalLabelCount) {
        this.errorMessage = 'Entered values must be unique';
        return;
      }

      if (!this.localEnumState.value) {
        this.localEnumState.value = nonEmptyValues[0].id; // default to something
      }
      this.localEnumState.values = this.localEnumState.values.filter(v => !v.isNew); // remove the empty value
      this.$emit('apply', this.localEnumState);
    },
    onCancel() {
      this.$emit('cancel');
      this.syncState();
    },
  }
}
</script>

<style scoped>
.el-form.custom-enum-state-form.el-form--inline {
  height: auto;
  display: flex;
  flex-direction: column;
}

.el-form-item {
  margin-right: 8px;
  margin-bottom: 8px;
}

.el-form-item:last-child {
  margin-bottom: 0;
  align-self: flex-end;
}

.value-row {
  padding-left: 16px;
  display: flex;
}

.error-message {
  color: #f92c2d;
  text-align: center;
}
</style>